/**
 * Live Quiz Screen Component
 * Main container that shows the appropriate view based on:
 * - Whether user is team captain or spectator
 * - Current session status (lobby, playing, rankings, completed)
 * - Connection status (connected, reconnecting)
 */

import React, { useMemo, useCallback, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { getLottieSource } from "@/lib/lottie-registry";
import { useQuizLive } from "../hooks/useQuizLive";
import { TeamLobby } from "./TeamLobby";
import { QuestionDisplay } from "./QuestionDisplay";
import { SpectatorView } from "./SpectatorView";
import { PausedModal } from "./PausedModal";
import { ConnectionLostModal } from "./ConnectionLostModal";
import { CountdownScreen } from "./CountdownScreen";
import { QuestionResultScreen } from "./QuestionResultScreen";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

interface LiveQuizScreenProps {
  /** Session ID to join */
  sessionId: string;
  /** Whether user is joining as spectator */
  spectatorMode?: boolean;
  /** Handler to close/exit the quiz */
  onExit: () => void;
}

/**
 * Main Live Quiz Screen for mobile players
 */
export const LiveQuizScreen: React.FC<LiveQuizScreenProps> = ({
  sessionId,
  spectatorMode = false,
  onExit,
}) => {
  const colors = useThemeColors();
  const { activeProfile, userTeam } = useAuth();
  
  // Initialize live quiz hook
  const {
    session,
    quiz,
    participants,
    rankings,
    currentQuestion,
    questionTimeRemaining,
    myParticipant,
    myAnswer,
    questionResult,
    questionPhase,
    countdownSeconds,
    participantResults,
    availableMascots,
    selectedMascot,
    isTeamCaptain,
    myTeamCaptain,
    isPaused,
    pausedForPlayer,
    pauseTimeRemaining,
    isLoading,
    isConnected,
    isReconnecting,
    manualRetryCount,
    error,
    joinSession,
    joinAsSpectator,
    selectMascot,
    setReady,
    submitAnswer,
    leaveSession,
    reconnect,
  } = useQuizLive({ sessionId, spectatorMode });
  
  // Note: isTeamCaptain is now provided by the hook
  // which checks if user is the team's registered player
  
  /**
   * Cleanup on unmount - the hook handles joining automatically
   */
  useEffect(() => {
    return () => {
      leaveSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  /**
   * Handle answer selection
   */
  const handleSelectAnswer = useCallback(async (optionIndex: number) => {
    await submitAnswer(optionIndex);
  }, [submitAnswer]);
  
  /**
   * Handle exit
   */
  const handleExit = useCallback(() => {
    leaveSession();
    onExit();
  }, [leaveSession, onExit]);
  
  /**
   * Determine current view to show
   */
  const renderContent = useMemo(() => {
    // Error state
    if (error) {
      return (
        <View className="flex-1 items-center justify-center p-6">
          <Ionicons name="warning" size={64} color="#EF4444" />
          <Text 
            className="text-xl font-bold mt-4 text-center"
            style={{ color: colors.text }}
          >
            Oeps!
          </Text>
          <Text 
            className="text-center mt-2"
            style={{ color: colors.textSecondary }}
          >
            {error}
          </Text>
          <TouchableOpacity
            onPress={handleExit}
            className="mt-6 px-6 py-3 rounded-full"
            style={{ backgroundColor: colors.primary }}
          >
            <Text className="text-white font-semibold">Terug</Text>
          </TouchableOpacity>
        </View>
      );
    }
    
    // Loading state
    if (!session || !quiz) {
      return (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color={colors.primary} />
          <Text 
            className="mt-4 text-lg"
            style={{ color: colors.textSecondary }}
          >
            Quiz laden...
          </Text>
        </View>
      );
    }
    
    // Spectator mode
    if (spectatorMode) {
      return (
        <SpectatorView
          session={session}
          participants={participants}
          currentQuestion={currentQuestion}
          timeRemaining={questionTimeRemaining}
          rankings={rankings}
          quizName={quiz.name}
          quizColor={quiz.color}
        />
      );
    }
    
    // Team player views based on session status
    switch (session.status) {
      case "lobby":
        return (
          <TeamLobby
            sessionId={sessionId}
            onLeave={handleExit}
          />
        );
        
      case "playing":
        // Use a stable key for the question view to prevent unnecessary remounts
        const questionKey = `question-${session.current_question_index}`;
        
        // Countdown phase - show "Get ready" screen
        if (questionPhase === "countdown") {
          return (
            <CountdownScreen
              key={`countdown-${questionKey}`}
              questionNumber={(session.current_question_index || 0) + 1}
              totalQuestions={session.total_questions}
              secondsRemaining={countdownSeconds}
              quizName={quiz.name}
              quizColor={quiz.color}
            />
          );
        }
        
        // Results phase - show who answered correctly
        if (questionPhase === "results" && currentQuestion && questionResult) {
          return (
            <QuestionResultScreen
              key={`results-${questionKey}`}
              question={currentQuestion}
              questionNumber={(session.current_question_index || 0) + 1}
              totalQuestions={session.total_questions}
              result={questionResult}
              participantResults={participantResults}
              participants={participants}
              quizColor={quiz.color}
              myAnswerCorrect={myAnswer?.is_correct}
            />
          );
        }
        
        // Active phase - show question
        if (currentQuestion) {
          return (
            <QuestionDisplay
              key={`active-${questionKey}`}
              question={currentQuestion}
              questionNumber={(session.current_question_index || 0) + 1}
              totalQuestions={session.total_questions}
              timeRemaining={questionTimeRemaining}
              isTeamCaptain={isTeamCaptain}
              selectedAnswer={myAnswer?.selected_option_index ?? null}
              isAnswerSubmitted={!!myAnswer}
              result={questionResult}
              isLoading={isLoading}
              onSelectAnswer={handleSelectAnswer}
              quizColor={quiz.color}
            />
          );
        }
        return null;
        
      case "section_break":
      case "showing_ranking":
        return (
          <View className="flex-1 items-center justify-center p-6">
            <LottieView
              source={getLottieSource("head_with_crown_emoji")}
              autoPlay
              loop
              style={{ width: 120, height: 120 }}
            />
            <Text 
              className="text-2xl font-bold mt-6"
              style={{ color: colors.text }}
            >
              🏆 Tussenstand
            </Text>
            <Text 
              className="text-center mt-2"
              style={{ color: colors.textSecondary }}
            >
              Kijk naar het scherm voor de stand!
            </Text>
            
            {/* Your team's position */}
            {myParticipant && (
              <View 
                className="mt-8 p-6 rounded-2xl items-center"
                style={{ backgroundColor: colors.surface }}
              >
                <Text style={{ color: colors.textSecondary }}>
                  Jouw team staat op
                </Text>
                <Text 
                  className="text-4xl font-bold mt-2"
                  style={{ color: quiz.color || colors.primary }}
                >
                  #{rankings.findIndex(r => r.participant.team_id === myParticipant.team_id) + 1 || "?"}
                </Text>
                <Text 
                  className="text-2xl font-semibold mt-2"
                  style={{ color: "#F59E0B" }}
                >
                  {myParticipant.total_score} punten
                </Text>
              </View>
            )}
          </View>
        );
        
      case "completed":
        const myRank = rankings.findIndex(r => r.participant.team_id === myParticipant?.team_id) + 1;
        const isWinner = myRank === 1;
        
        return (
          <View className="flex-1 items-center justify-center p-6">
            {isWinner && (
              <LottieView
                source={getLottieSource("confetti")}
                autoPlay
                loop={false}
                style={{ 
                  position: "absolute",
                  width: SCREEN_WIDTH,
                  height: SCREEN_HEIGHT,
                }}
              />
            )}
            
            <LottieView
              source={getLottieSource(isWinner ? "head_with_crown_emoji" : "Success")}
              autoPlay
              loop={isWinner}
              style={{ width: 150, height: 150 }}
            />
            
            <Text 
              className="text-3xl font-bold mt-4 text-center"
              style={{ color: colors.text }}
            >
              {isWinner ? "🎉 Jullie hebben gewonnen!" : "Quiz Afgelopen!"}
            </Text>
            
            <View 
              className="mt-6 p-6 rounded-2xl items-center"
              style={{ backgroundColor: colors.surface }}
            >
              <Text style={{ color: colors.textSecondary }}>
                Eindstand
              </Text>
              <Text 
                className="text-5xl font-bold mt-2"
                style={{ color: quiz.color || colors.primary }}
              >
                #{myRank || "?"}
              </Text>
              {myParticipant && (
                <Text 
                  className="text-xl font-semibold mt-2"
                  style={{ color: "#F59E0B" }}
                >
                  {myParticipant.total_score} punten
                </Text>
              )}
            </View>
            
            <TouchableOpacity
              onPress={handleExit}
              className="mt-8 px-8 py-4 rounded-full"
              style={{ backgroundColor: colors.primary }}
            >
              <Text className="text-white font-bold text-lg">
                Afsluiten
              </Text>
            </TouchableOpacity>
          </View>
        );
        
      default:
        return null;
    }
  }, [
    session,
    quiz,
    participants,
    rankings,
    currentQuestion,
    questionTimeRemaining,
    myParticipant,
    myAnswer,
    questionResult,
    questionPhase,
    countdownSeconds,
    participantResults,
    isTeamCaptain,
    isLoading,
    error,
    spectatorMode,
    colors,
    sessionId,
    handleSelectAnswer,
    handleExit,
  ]);
  
  return (
    <SafeAreaView 
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      {/* Header */}
      <View 
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderBottomColor: colors.border }}
      >
        <TouchableOpacity
          onPress={handleExit}
          className="p-2"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="close" size={24} color={colors.text} />
        </TouchableOpacity>
        
        <View className="flex-row items-center">
          <View 
            className={`w-2 h-2 rounded-full mr-2 ${isConnected ? "bg-green-500" : "bg-red-500"}`}
          />
          <Text className="font-medium" style={{ color: colors.text }}>
            {quiz?.name || "Live Quiz"}
          </Text>
        </View>
        
        <View className="w-10" />
      </View>
      
      {/* Main content */}
      {renderContent}
      
      {/* Paused Modal - shown when a team captain disconnects */}
      <PausedModal
        visible={isPaused && !spectatorMode}
        playerName={pausedForPlayer || "Onbekend"}
        timeRemaining={pauseTimeRemaining}
        isAdmin={false}
      />
      
      {/* Connection Lost Modal (player-facing, with manual reconnect button) */}
      {!spectatorMode && (
        <ConnectionLostModal
          isConnected={isConnected}
          isReconnecting={isReconnecting}
          manualRetryCount={manualRetryCount}
          hasSession={!!session}
          onReconnect={reconnect}
        />
      )}
    </SafeAreaView>
  );
};

export default LiveQuizScreen;

